import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { saveAs } from 'file-saver';
import { useUser } from '../../contexts/UserContext';
import { extractKeywords } from '../../services/openaiService';
import { parseResumeFile, testResumeParsingWithText } from '../../services/resumeParserService';
import templateService from '../../services/templateService';
import { useFeatureGate } from '../../hooks/useFeatureGate';
import FreeCreditsLabel from '../../components/FreeCreditsLabel';
import {
  FileTextIcon,
  CheckCircleIcon,
  AlertTriangleIcon,
  SendIcon,
  XIcon,
  ChevronUpIcon,
  UserIcon,
  BriefcaseIcon,
  AwardIcon,
  LayoutGridIcon,
  SparklesIcon,
  RocketIcon,
} from '../icons';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3001';

/* ─────────────────────────────────────────────────────────────────────────
 * Resume Builder — Electron-native pane.
 *
 * Same backend + services as the website's ResumeBuilderStudio:
 *   GET  /api/profile               (load profile data)
 *   POST /api/resume/deduct-credit  (1 credit per parse / use-profile)
 *   extractKeywords()               (openaiService — JD → keyword list)
 *   parseResumeFile()               (resumeParserService — PDF/DOCX → data)
 *   testResumeParsingWithText()     (re-parse text against keywords)
 *   templateService.generateFromTemplate(id, data) → DOCX Blob
 *
 * Layout:
 *   Left column  — JD bar, source picker, accordion sections.
 *   Right column — template picker, ATS score ring, download.
 * ─────────────────────────────────────────────────────────────────────── */

const TEMPLATES = [
  { id: 1, name: 'Professional', accent: '#a78bfa' },
  { id: 2, name: 'Modern',       accent: '#22d3ee' },
  { id: 3, name: 'Creative',     accent: '#ec4899' },
  { id: 4, name: 'Academic',     accent: '#10b981' },
];

const SECTIONS = [
  { id: 'personal',       title: 'Personal Info',         Icon: UserIcon },
  { id: 'summary',        title: 'Professional Summary',  Icon: FileTextIcon },
  { id: 'experience',     title: 'Work Experience',       Icon: BriefcaseIcon },
  { id: 'education',      title: 'Education',             Icon: AwardIcon },
  { id: 'skills',         title: 'Skills',                Icon: SparklesIcon },
  { id: 'projects',       title: 'Projects',              Icon: RocketIcon },
  { id: 'certifications', title: 'Certifications',        Icon: AwardIcon },
];

function buildResumeTextFromProfile(profile) {
  if (!profile) return '';
  let text = '';
  if (profile.fullName) text += `${profile.fullName}\n`;
  if (profile.jobTitle) text += `${profile.jobTitle}\n`;
  if (profile.phone) text += `Phone: ${profile.phone}\n`;
  if (profile.location) text += `Location: ${profile.location}\n`;
  if (profile.linkedIn) text += `LinkedIn: ${profile.linkedIn}\n`;
  if (profile.github) text += `GitHub: ${profile.github}\n`;
  text += '\n';
  if (profile.professionalSummary) text += `PROFESSIONAL SUMMARY\n${profile.professionalSummary}\n\n`;
  if (profile.skills) text += `SKILLS\n${profile.skills}\n\n`;
  if (Array.isArray(profile.workExperience) && profile.workExperience.some(e => e.company || e.position)) {
    text += 'WORK EXPERIENCE\n';
    profile.workExperience.forEach(exp => {
      if (exp.position || exp.company) {
        text += `${exp.position || ''}${exp.company ? ` at ${exp.company}` : ''}${exp.duration ? ` (${exp.duration})` : ''}\n`;
        if (exp.description) text += `${exp.description}\n`;
        text += '\n';
      }
    });
  }
  if (Array.isArray(profile.education) && profile.education.some(e => e.degree || e.institution)) {
    text += 'EDUCATION\n';
    profile.education.forEach(edu => {
      if (edu.degree || edu.institution) {
        text += `${edu.degree || ''}${edu.field ? ` in ${edu.field}` : ''}${edu.institution ? ` - ${edu.institution}` : ''}${edu.year ? ` (${edu.year})` : ''}\n`;
      }
    });
    text += '\n';
  }
  if (Array.isArray(profile.projects) && profile.projects.some(p => p.name)) {
    text += 'PROJECTS\n';
    profile.projects.forEach(proj => {
      if (proj.name) {
        text += `${proj.name}${proj.technologies ? ` (${proj.technologies})` : ''}\n`;
        if (proj.description) text += `${proj.description}\n`;
        text += '\n';
      }
    });
  }
  if (profile.certifications) text += `CERTIFICATIONS\n${profile.certifications}\n`;
  return text.trim();
}

export default function ResumeBuilderPane() {
  const fileRef = useRef(null);
  const { user, credits, setCredits, fetchCredits } = useUser();

  // Form data
  const [personal, setPersonal] = useState({
    fullName: '', email: '', phone: '', location: '', linkedIn: '', portfolio: '',
  });
  const [summary, setSummary] = useState('');
  const [experiences, setExperiences] = useState([{ id: 1, company: '', position: '', duration: '', description: '' }]);
  const [education, setEducation] = useState([{ id: 1, institution: '', degree: '', field: '', year: '' }]);
  const [skills, setSkills] = useState('');
  const [projects, setProjects] = useState([{ id: 1, name: '', technologies: '', description: '' }]);
  const [certifications, setCertifications] = useState('');

  // Workflow state
  const [jobDescription, setJobDescription] = useState('');
  const [keywords, setKeywords] = useState([]);
  const [extracting, setExtracting] = useState(false);
  const [parsing, setParsing] = useState(false);
  const [parseError, setParseError] = useState('');
  const [uploadedName, setUploadedName] = useState('');
  const [usingProfile, setUsingProfile] = useState(false);
  const [openSection, setOpenSection] = useState('personal');
  const [selectedTemplate, setSelectedTemplate] = useState(1);
  const [downloading, setDownloading] = useState(false);
  const [downloadErr, setDownloadErr] = useState('');
  const [profileData, setProfileData] = useState(null);
  const [insufficientCredits, setInsufficientCredits] = useState(false);

  // Load profile once on mount.
  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    fetch(`${API_BASE_URL}/api/profile`, {
      headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
    })
      .then(r => r.ok ? r.json() : null)
      .then(d => { if (!cancelled && d?.profile) setProfileData(d.profile); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [user]);

  /* ── Credits ──────────────────────────────────────────────────────── */
  // Shared gate: handles the deduct call, paywall trigger, and keeps the
  // per-feature counter chip in sync. We still surface the insufficient-
  // credits modal locally so users see a contextual explanation instead
  // of just the generic pricing modal.
  const gate = useFeatureGate('resumeBuilder');

  const requireCredits = useCallback(() => {
    if (gate.locked) {
      setInsufficientCredits(true);
      return false;
    }
    return true;
  }, [gate.locked]);

  const deductCredit = useCallback(async () => {
    const { ok, reason, remaining } = await gate.deduct();
    if (!ok) {
      if (reason === 'no-credits') setInsufficientCredits(true);
      else setParseError(`Credit error: ${reason || 'unknown'}`);
      return false;
    }
    if (setCredits && typeof remaining === 'number' && Number.isFinite(remaining)) {
      setCredits(prev => Math.max(0, (prev || 0) - 1));
    }
    return true;
  }, [gate, setCredits]);

  /* ── List helpers ─────────────────────────────────────────────────── */
  const updateList = (setter) => (id, field, val) =>
    setter(prev => prev.map(it => it.id === id ? { ...it, [field]: val } : it));
  const addItem = (setter, defaults) => () =>
    setter(prev => [...prev, { id: Date.now(), ...defaults }]);
  const removeItem = (setter) => (id) =>
    setter(prev => prev.length > 1 ? prev.filter(it => it.id !== id) : prev);

  const updateExp = updateList(setExperiences);
  const addExp    = addItem(setExperiences, { company: '', position: '', duration: '', description: '' });
  const removeExp = removeItem(setExperiences);

  const updateEdu = updateList(setEducation);
  const addEdu    = addItem(setEducation, { institution: '', degree: '', field: '', year: '' });
  const removeEdu = removeItem(setEducation);

  const updateProj = updateList(setProjects);
  const addProj    = addItem(setProjects, { name: '', technologies: '', description: '' });
  const removeProj = removeItem(setProjects);

  /* ── Keyword extraction ───────────────────────────────────────────── */
  const handleExtractKeywords = async () => {
    if (!jobDescription.trim()) {
      setParseError('Paste a job description first.');
      return;
    }
    setParseError('');
    setExtracting(true);
    try {
      const kws = await extractKeywords(jobDescription);
      setKeywords(Array.isArray(kws) ? kws : []);
    } catch (err) {
      setParseError(`Could not extract keywords: ${err.message}`);
    } finally {
      setExtracting(false);
    }
  };

  /* ── Parse / fill form ────────────────────────────────────────────── */
  const fillFromParsed = useCallback((d) => {
    if (d.personalInfo) {
      setPersonal({
        fullName: d.personalInfo.name || d.personalInfo.fullName || '',
        email:    d.personalInfo.email || '',
        phone:    d.personalInfo.phone || '',
        location: d.personalInfo.location || d.personalInfo.address || '',
        linkedIn: d.personalInfo.linkedin || '',
        portfolio: d.personalInfo.portfolio || d.personalInfo.website || '',
      });
    }
    if (d.summary || d.professionalSummary) setSummary(d.summary || d.professionalSummary);
    if (Array.isArray(d.workExperience) && d.workExperience.length) {
      setExperiences(d.workExperience.map((e, i) => ({
        id: i + 1,
        company:    e.company || e.employer || '',
        position:   e.position || e.title || e.role || '',
        duration:   e.duration || e.dates || '',
        description: Array.isArray(e.description) ? e.description.join('\n') : (e.description || e.responsibilities || ''),
      })));
    }
    if (Array.isArray(d.education) && d.education.length) {
      const valid = d.education.filter(e => e && (e.institution || e.degree));
      if (valid.length) {
        setEducation(valid.map((e, i) => ({
          id: i + 1,
          institution: e.institution || e.school || '',
          degree:      e.degree || e.qualification || '',
          field:       e.field || e.major || '',
          year:        e.year || e.dates || '',
        })));
      }
    }
    if (d.skills) {
      let s = '';
      if (typeof d.skills === 'object' && !Array.isArray(d.skills)) {
        Object.entries(d.skills).forEach(([cat, list]) => {
          if (list && String(list).trim()) s += `• ${cat}: ${list}\n`;
        });
      } else {
        s = Array.isArray(d.skills) ? d.skills.join(', ') : d.skills;
      }
      setSkills(s.trim());
    }
    if (Array.isArray(d.projects) && d.projects.length) {
      setProjects(d.projects.map((p, i) => ({
        id: i + 1,
        name:         p.name || p.title || '',
        technologies: Array.isArray(p.technologies) ? p.technologies.join(', ') : (p.technologies || p.tech || ''),
        description:  Array.isArray(p.description) ? p.description.join('\n') : (p.description || ''),
      })));
    }
    if (d.certifications) {
      let c = '';
      if (Array.isArray(d.certifications)) {
        c = d.certifications.map(x => typeof x === 'string' ? x : x?.name || '').filter(Boolean).join(', ');
      } else if (typeof d.certifications === 'string') {
        c = d.certifications;
      }
      setCertifications(c);
    }
  }, []);

  const handleFile = useCallback(async (file) => {
    if (!file) return;
    const allowed = [
      'application/pdf',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'application/msword',
    ];
    if (!allowed.includes(file.type) && !/\.(pdf|docx?)$/i.test(file.name)) {
      setParseError('Upload PDF or DOCX only.');
      return;
    }
    if (!requireCredits()) return;

    setParsing(true);
    setParseError('');
    setUploadedName(file.name);
    setUsingProfile(false);
    try {
      const ok = await deductCredit();
      if (!ok) { setParsing(false); return; }
      const data = await parseResumeFile(file, jobDescription, keywords);
      if (data) {
        fillFromParsed(data);
        setOpenSection('personal');
        if (fetchCredits) fetchCredits();
      } else {
        setParseError('Could not extract data from the file.');
      }
    } catch (err) {
      setParseError(`Parse failed: ${err.message}`);
    } finally {
      setParsing(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  }, [jobDescription, keywords, fillFromParsed, requireCredits, deductCredit, fetchCredits]);

  const onFileInput = (e) => handleFile(e.target.files?.[0]);
  const onDrop = (e) => { e.preventDefault(); handleFile(e.dataTransfer.files?.[0]); };
  const onDragOver = (e) => e.preventDefault();

  const startScratch = () => {
    setUploadedName('');
    setParseError('');
    setOpenSection('personal');
    setUsingProfile(false);
  };

  const handleUseProfile = useCallback(async () => {
    if (!profileData) return;
    if (!requireCredits()) return;

    setParsing(true);
    setParseError('');
    setUploadedName(`${profileData.fullName || 'Profile'} (from Profile)`);
    setUsingProfile(true);
    try {
      const ok = await deductCredit();
      if (!ok) { setParsing(false); return; }
      const resumeText = buildResumeTextFromProfile(profileData);
      const data = keywords.length > 0
        ? await testResumeParsingWithText(resumeText, jobDescription, keywords)
        : {
            personalInfo: {
              name:     profileData.fullName || '',
              email:    profileData.email || user?.email || '',
              phone:    profileData.phone || '',
              location: profileData.location || '',
              linkedin: profileData.linkedIn || '',
              portfolio: profileData.portfolio || profileData.github || '',
            },
            summary: profileData.professionalSummary || '',
            workExperience: Array.isArray(profileData.workExperience) ? profileData.workExperience : [],
            education:      Array.isArray(profileData.education) ? profileData.education : [],
            skills:         profileData.skills || '',
            projects:       Array.isArray(profileData.projects) ? profileData.projects : [],
            certifications: profileData.certifications || '',
          };
      if (data) {
        fillFromParsed(data);
        setOpenSection('personal');
        if (fetchCredits) fetchCredits();
      }
    } catch (err) {
      setParseError(`Profile load failed: ${err.message}`);
    } finally {
      setParsing(false);
    }
  }, [profileData, requireCredits, deductCredit, jobDescription, keywords, fillFromParsed, user, fetchCredits]);

  /* ── Download (DOCX) ──────────────────────────────────────────────── */
  const prepareData = useCallback(() => ({
    personalInfo: {
      name:     personal.fullName,
      email:    personal.email,
      phone:    personal.phone,
      location: personal.location,
      linkedin: personal.linkedIn,
      portfolio: personal.portfolio,
    },
    fullName: personal.fullName,
    name:     personal.fullName,
    email:    personal.email,
    phone:    personal.phone,
    location: personal.location,
    linkedin: personal.linkedIn,
    portfolio: personal.portfolio,
    summary,
    professionalSummary: summary,
    workExperience: experiences,
    workExperiences: experiences,
    education,
    skills,
    projects,
    certifications,
  }), [personal, summary, experiences, education, skills, projects, certifications]);

  const handleDownload = async () => {
    setDownloadErr('');
    setDownloading(true);
    try {
      const blob = await templateService.generateFromTemplate(selectedTemplate, prepareData());
      const fname = (personal.fullName || 'Resume').replace(/\s+/g, '_');
      saveAs(blob, `${fname}_Resume.docx`);
    } catch (err) {
      setDownloadErr(`Download failed: ${err.message}`);
    } finally {
      setDownloading(false);
    }
  };

  /* ── ATS heuristic ────────────────────────────────────────────────── */
  const atsScore = useMemo(() => {
    let s = 0;
    if (personal.fullName) s += 8;
    if (personal.email)    s += 6;
    if (personal.phone)    s += 4;
    if (personal.location) s += 4;
    if (personal.linkedIn) s += 4;
    if (summary && summary.length > 60) s += 14;
    s += Math.min(20, experiences.filter(e => e.company || e.position).length * 7);
    s += Math.min(10, education.filter(e => e.institution || e.degree).length * 5);
    if (skills && skills.length > 30) s += 14;
    s += Math.min(8, projects.filter(p => p.name).length * 4);
    if (certifications) s += 4;
    if (keywords.length) {
      const blob = JSON.stringify({ summary, skills, experiences, projects }).toLowerCase();
      const hits = keywords.filter(k => blob.includes(String(k).toLowerCase())).length;
      s += Math.min(8, Math.round((hits / keywords.length) * 8));
    }
    return Math.min(99, s);
  }, [personal, summary, experiences, education, skills, projects, certifications, keywords]);

  const sectionDone = (id) => {
    switch (id) {
      case 'personal':       return !!(personal.fullName && personal.email);
      case 'summary':        return summary.length > 30;
      case 'experience':     return experiences.some(e => e.company && e.position);
      case 'education':      return education.some(e => e.institution && e.degree);
      case 'skills':         return skills.length > 10;
      case 'projects':       return projects.some(p => p.name);
      case 'certifications': return certifications.length > 0;
      default: return false;
    }
  };

  const accent = TEMPLATES.find(t => t.id === selectedTemplate)?.accent || '#a78bfa';

  return (
    <div className="rbp-root">
      {/* Header */}
      <div className="pane">
        <div className="pane-eyebrow">
          <FileTextIcon size={11} /> Resume Studio
        </div>
        <h1 className="pane-title">Resume Builder</h1>
      </div>

      {/* Required JD bar + keyword chips. Every downstream action — file
          parse, Use Profile data, Start from scratch, Download — is gated
          on this textarea having content, so we mark it required and
          surface a hint when it's empty. */}
      <div className="rbp-jd-label-row">
        <span className="rbp-jd-label">
          Job description <span className="rbp-required">*</span>
        </span>
        {!jobDescription.trim() && (
          <span className="rbp-jd-hint">
            Required to parse a resume, load profile, or download.
          </span>
        )}
      </div>
      <div className={`rbp-jd-card ${!jobDescription.trim() ? 'is-required' : ''}`}>
        <textarea
          className="pane-textarea"
          placeholder="Paste the job description to tailor parsing & ATS scoring."
          value={jobDescription}
          onChange={e => setJobDescription(e.target.value)}
          rows={2}
          required
        />
        <button
          type="button"
          className="pane-cta-secondary"
          onClick={handleExtractKeywords}
          disabled={extracting || !jobDescription.trim()}
        >
          {extracting ? 'Extracting…' : 'Extract keywords'}
        </button>
      </div>
      {keywords.length > 0 && (
        <div className="rbp-kwbar">
          <span className="rbp-kwbar-label">Targeting:</span>
          {keywords.map(k => <span key={k} className="rbp-kw">{k}</span>)}
        </div>
      )}

      {/* Two-column workspace */}
      <div className="rbp-workspace" style={{ '--rbp-accent': accent }}>
        {/* LEFT: source + accordion */}
        <div className="rbp-left">
          <div className="rbp-source">
            {(() => {
              const jdMissing = !jobDescription.trim();
              const dropDisabled = parsing || jdMissing || gate.locked;
              return (
                <>
                  <div
                    className={`rbp-drop ${parsing ? 'busy' : ''} ${jdMissing ? 'disabled' : ''}`}
                    onDrop={dropDisabled ? (e) => e.preventDefault() : onDrop}
                    onDragOver={onDragOver}
                    onClick={dropDisabled ? undefined : () => fileRef.current?.click()}
                    role="button"
                    tabIndex={0}
                    aria-disabled={dropDisabled}
                  >
                    <input
                      ref={fileRef}
                      type="file"
                      accept=".pdf,.docx,.doc,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                      hidden
                      onChange={onFileInput}
                      disabled={dropDisabled}
                    />
                    <div className="rbp-drop-icon">
                      <FileTextIcon size={22} />
                    </div>
                    <div className="rbp-drop-title">
                      {parsing ? 'Parsing…' : uploadedName ? `Loaded: ${uploadedName}` : <>Upload PDF or DOCX <FreeCreditsLabel feature="resumeBuilder" /></>}
                    </div>
                    <div className="rbp-drop-sub">
                      {parsing
                        ? 'Extracting structure & keywords'
                        : jdMissing
                          ? 'Paste the job description above first.'
                          : 'Drag & drop or click — AI auto-fills the form'}
                    </div>
                  </div>
                  <div className="rbp-source-actions">
                    {profileData && (
                      <button
                        type="button"
                        className="rbp-source-btn"
                        onClick={handleUseProfile}
                        disabled={parsing || jdMissing || gate.locked}
                        title={gate.locked
                          ? 'Out of free credits — upgrade to keep using'
                          : jdMissing ? 'Paste the job description first' : ''}
                      >
                        <UserIcon size={13} /> Use Profile data <FreeCreditsLabel feature="resumeBuilder" />
                      </button>
                    )}
                    <button
                      type="button"
                      className="rbp-source-btn"
                      onClick={startScratch}
                      disabled={parsing || jdMissing}
                      title={jdMissing ? 'Paste the job description first' : ''}
                    >
                      Start from scratch
                    </button>
                  </div>
                </>
              );
            })()}
          </div>

          {parseError && <div className="pane-error">{parseError}</div>}
          {usingProfile && <div className="rbp-info"><CheckCircleIcon size={13} /> Loaded from your profile</div>}

          {/* Accordion */}
          <div className="rbp-accordion">
            {SECTIONS.map(s => {
              const open = openSection === s.id;
              const done = sectionDone(s.id);
              const Icon = s.Icon;
              return (
                <div key={s.id} className={`rbp-acc ${open ? 'open' : ''}`}>
                  <button
                    type="button"
                    className="rbp-acc-head"
                    onClick={() => setOpenSection(open ? null : s.id)}
                  >
                    <Icon size={14} />
                    <span className="rbp-acc-title">{s.title}</span>
                    {done && <CheckCircleIcon size={13} />}
                    <span className={`rbp-acc-chev ${open ? 'open' : ''}`}><ChevronUpIcon size={14} /></span>
                  </button>
                  {open && (
                    <div className="rbp-acc-body">
                      {s.id === 'personal' && (
                        <div className="rbp-grid-2">
                          <Field label="Full Name"  value={personal.fullName} onChange={v => setPersonal(p => ({ ...p, fullName: v }))} placeholder="Jane Doe" />
                          <Field label="Email"      value={personal.email}    onChange={v => setPersonal(p => ({ ...p, email: v }))}    placeholder="jane@email.com" type="email" />
                          <Field label="Phone"      value={personal.phone}    onChange={v => setPersonal(p => ({ ...p, phone: v }))}    placeholder="(555) 555-1234" />
                          <Field label="Location"   value={personal.location} onChange={v => setPersonal(p => ({ ...p, location: v }))} placeholder="San Francisco, CA" />
                          <Field label="LinkedIn"   value={personal.linkedIn} onChange={v => setPersonal(p => ({ ...p, linkedIn: v }))} placeholder="linkedin.com/in/janedoe" />
                          <Field label="Portfolio"  value={personal.portfolio} onChange={v => setPersonal(p => ({ ...p, portfolio: v }))} placeholder="janedoe.dev" />
                        </div>
                      )}

                      {s.id === 'summary' && (
                        <TextArea
                          label="Professional Summary"
                          value={summary}
                          onChange={setSummary}
                          rows={5}
                          placeholder="3-4 sentences. Lead with role + years + your strongest impact metric."
                        />
                      )}

                      {s.id === 'experience' && (
                        <div className="rbp-list">
                          {experiences.map((exp, i) => (
                            <div key={exp.id} className="rbp-item">
                              <div className="rbp-item-head">
                                <span>Experience #{i + 1}</span>
                                <button type="button" className="rbp-rm" onClick={() => removeExp(exp.id)} disabled={experiences.length <= 1}>
                                  <XIcon size={11} /> Remove
                                </button>
                              </div>
                              <div className="rbp-grid-2">
                                <Field label="Company"  value={exp.company}  onChange={v => updateExp(exp.id, 'company', v)}  placeholder="Acme Inc." />
                                <Field label="Position" value={exp.position} onChange={v => updateExp(exp.id, 'position', v)} placeholder="Senior Engineer" />
                                <Field label="Duration" value={exp.duration} onChange={v => updateExp(exp.id, 'duration', v)} placeholder="Jan 2022 – Present" />
                              </div>
                              <TextArea
                                label="What you did (one bullet per line)"
                                value={exp.description}
                                onChange={v => updateExp(exp.id, 'description', v)}
                                rows={4}
                                placeholder="• Shipped X, cutting Y by Z%…"
                              />
                            </div>
                          ))}
                          <button type="button" className="rbp-add" onClick={addExp}>+ Add experience</button>
                        </div>
                      )}

                      {s.id === 'education' && (
                        <div className="rbp-list">
                          {education.map((edu, i) => (
                            <div key={edu.id} className="rbp-item">
                              <div className="rbp-item-head">
                                <span>Education #{i + 1}</span>
                                <button type="button" className="rbp-rm" onClick={() => removeEdu(edu.id)} disabled={education.length <= 1}>
                                  <XIcon size={11} /> Remove
                                </button>
                              </div>
                              <div className="rbp-grid-2">
                                <Field label="Institution" value={edu.institution} onChange={v => updateEdu(edu.id, 'institution', v)} placeholder="University of …" />
                                <Field label="Degree"      value={edu.degree}      onChange={v => updateEdu(edu.id, 'degree', v)}      placeholder="BSc" />
                                <Field label="Field"       value={edu.field}       onChange={v => updateEdu(edu.id, 'field', v)}       placeholder="Computer Science" />
                                <Field label="Year"        value={edu.year}        onChange={v => updateEdu(edu.id, 'year', v)}        placeholder="2018 – 2022" />
                              </div>
                            </div>
                          ))}
                          <button type="button" className="rbp-add" onClick={addEdu}>+ Add education</button>
                        </div>
                      )}

                      {s.id === 'skills' && (
                        <TextArea
                          label="Skills (comma-separated or grouped per line)"
                          value={skills}
                          onChange={setSkills}
                          rows={5}
                          placeholder="React, TypeScript, Node.js, AWS, PostgreSQL, Docker, CI/CD…"
                        />
                      )}

                      {s.id === 'projects' && (
                        <div className="rbp-list">
                          {projects.map((p, i) => (
                            <div key={p.id} className="rbp-item">
                              <div className="rbp-item-head">
                                <span>Project #{i + 1}</span>
                                <button type="button" className="rbp-rm" onClick={() => removeProj(p.id)} disabled={projects.length <= 1}>
                                  <XIcon size={11} /> Remove
                                </button>
                              </div>
                              <div className="rbp-grid-2">
                                <Field label="Name"         value={p.name}         onChange={v => updateProj(p.id, 'name', v)}         placeholder="Project Name" />
                                <Field label="Technologies" value={p.technologies} onChange={v => updateProj(p.id, 'technologies', v)} placeholder="Next.js, OpenAI" />
                              </div>
                              <TextArea
                                label="Description"
                                value={p.description}
                                onChange={v => updateProj(p.id, 'description', v)}
                                rows={3}
                                placeholder="What you built and the impact"
                              />
                            </div>
                          ))}
                          <button type="button" className="rbp-add" onClick={addProj}>+ Add project</button>
                        </div>
                      )}

                      {s.id === 'certifications' && (
                        <TextArea
                          label="Certifications"
                          value={certifications}
                          onChange={setCertifications}
                          rows={3}
                          placeholder="AWS Solutions Architect, …"
                        />
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* RIGHT: template + score + download */}
        <div className="rbp-right">
          <div className="rbp-rightcard">
            <div className="rbp-tpl-row">
              <span className="rbp-tpl-label">Template</span>
              <div className="rbp-tpl-pills">
                {TEMPLATES.map(t => (
                  <button
                    key={t.id}
                    type="button"
                    className={`rbp-tpl ${selectedTemplate === t.id ? 'active' : ''}`}
                    style={{ '--accent': t.accent }}
                    onClick={() => setSelectedTemplate(t.id)}
                  >
                    {t.name}
                  </button>
                ))}
              </div>
            </div>

            <ScoreRing value={atsScore} accent={accent} />

            <button
              type="button"
              className="pane-cta rbp-download"
              onClick={handleDownload}
              disabled={downloading || !jobDescription.trim()}
              title={!jobDescription.trim() ? 'Paste the job description first' : ''}
            >
              {downloading
                ? 'Building DOCX…'
                : !jobDescription.trim()
                  ? <>Paste JD to enable download</>
                  : <>Download Resume (.docx) <SendIcon size={13} /></>}
            </button>
            {downloadErr && <div className="pane-error" style={{ marginTop: 10 }}>{downloadErr}</div>}
          </div>
        </div>
      </div>

      {insufficientCredits && (
        <div className="rbp-modal-bg" onClick={() => setInsufficientCredits(false)}>
          <div className="rbp-modal" onClick={e => e.stopPropagation()}>
            <div className="rbp-modal-head">
              <h3>Out of credits</h3>
              <button type="button" className="ilv-modal-close" onClick={() => setInsufficientCredits(false)}>
                <XIcon size={18} />
              </button>
            </div>
            <p>You need at least 1 credit to parse a resume.</p>
            <p>Current credits: <strong>{credits}</strong></p>
            <div className="rbp-modal-actions">
              <button type="button" className="pane-cta-secondary" onClick={() => setInsufficientCredits(false)}>Close</button>
              <button
                type="button"
                className="pane-cta"
                onClick={() => {
                  setInsufficientCredits(false);
                  document.dispatchEvent(new CustomEvent('openPricingModal'));
                }}
              >
                Get more credits
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/* ─── Helpers ─────────────────────────────────────────────────────────── */

function Field({ label, value, onChange, placeholder, type = 'text' }) {
  return (
    <label className="rbp-field">
      <span className="pane-label">{label}</span>
      <input
        type={type}
        className="pane-input"
        value={value || ''}
        onChange={e => onChange(e.target.value)}
        placeholder={placeholder}
      />
    </label>
  );
}

function TextArea({ label, value, onChange, placeholder, rows = 4 }) {
  return (
    <label className="rbp-field">
      <span className="pane-label">{label}</span>
      <textarea
        className="pane-textarea"
        value={value || ''}
        onChange={e => onChange(e.target.value)}
        placeholder={placeholder}
        rows={rows}
      />
    </label>
  );
}

function ScoreRing({ value, accent }) {
  const pct = Math.max(0, Math.min(100, value || 0));
  const circ = 2 * Math.PI * 38;
  const dash = (pct / 100) * circ;
  return (
    <div className="rbp-score">
      <svg viewBox="0 0 100 100" width="78" height="78" aria-hidden="true">
        <circle cx="50" cy="50" r="38" fill="none" stroke="rgba(255,255,255,0.07)" strokeWidth="9" />
        <circle
          cx="50" cy="50" r="38" fill="none"
          stroke={accent} strokeWidth="9" strokeLinecap="round"
          strokeDasharray={`${dash} ${circ}`}
          transform="rotate(-90 50 50)"
        />
      </svg>
      <div className="rbp-score-meta">
        <div className="rbp-score-val">{pct}<span>/100</span></div>
        <div className="rbp-score-label">ATS readiness</div>
      </div>
    </div>
  );
}

